import { yupResolver } from '@hookform/resolvers/yup';
import { Backdrop, Box, CircularProgress, Grid, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState, useMemo, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getOTPExpiryTime, IOtpRequest, workSpaceMFALogin } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  Item,
  WisestepLogo,
} from 'wisestep-base';
import { object, string } from 'yup';

import { AuthLayout } from '../../../components';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import CountDownTime from './CountDownTime';

const validationSchema = object().shape({
  otp: string().min(4, 'OTP must be at least 4 characters').required('OTP is required'),
});

const StyledGrid = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  rowGap: 16,
}));

const OTPNotificaiton = ({ text, isSuccess }: { text: string; isSuccess: boolean }) => (
  <Grid container>
    <StyledGrid item container xs={12}>
      <WsTypography
        color={isSuccess ? WsTypoColors.PRIMARY600 : WsTypoColors.ERROR}
        variant={WsTypoVariants.BODY1}
      >
        {text}
      </WsTypography>
    </StyledGrid>
  </Grid>
);

export const Loader = () => (
  <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
    <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
  </Backdrop>
);

const OtpPageLayout = () => {
  const navigate = useNavigate();
  const formOptions = {
    resolver: yupResolver(validationSchema),
  };
  const [pageLoaded, setPageLoaded] = useState(false);
  const methods = useForm<IOtpRequest>(formOptions);
  const [time, setTime] = useState<number | null>(null);
  const [resendTime, setResendTime] = useState(60);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [notificationState, setNotificationState] = useState<{
    otpError?: boolean;
    resendOtp?: boolean;
  }>({});

  const [isLoading, setIsLoading] = useState(false);
  const { handleSubmit, reset } = methods;

  const getExpiryTime = async () => {
    try {
      setTimeout(async () => {
        const time = await getOTPExpiryTime();
        setTime(time.data);
        setPageLoaded(true);
      }, 200);
    } catch (e) {
      setTime(null);
      setPageLoaded(true);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await workSpaceMFALogin({ requestotp: true });
        getExpiryTime();
      } catch (e) {
        getExpiryTime();
      }
    })();
  }, []);

  useEffect(() => {
    if (resendTime === 0) {
      return;
    }
    setTimeout(() => {
      setResendTime(resendTime - 1);
    }, 1000);
  }, [resendTime]);

  const onOTPSubmit = async (data: IOtpRequest) => {
    try {
      setIsLoading(true);
      await workSpaceMFALogin(data);
      setIsLoading(false);
      setNotificationState({ otpError: false });
      navigate('/home');
      window.location.reload();
    } catch (e) {
      setIsLoading(false);
      setNotificationState({
        otpError: true,
      });
      reset({ otp: '' });
    }
  };

  const onResendOTP = async () => {
    const data: IOtpRequest = {
      resendotp: true,
    };
    try {
      await workSpaceMFALogin(data);
      window.location.reload();
      setNotificationState({ resendOtp: true });
      setNotification(success('OTP resent'));
    } catch (e) {
      setNotificationState({ resendOtp: false });
    }
  };

  const showNotification = useMemo(() => {
    if (notificationState.otpError !== undefined) {
      if (notificationState.otpError) {
        return <OTPNotificaiton text="Invalid OTP entered" isSuccess={false} />;
      } else {
        return <OTPNotificaiton text="OTP validated successfully" isSuccess={true} />;
      }
    }
    if (
      notificationState.resendOtp !== undefined &&
      !notificationState.resendOtp &&
      resendTime > 0
    ) {
      return <OTPNotificaiton text="Error while sending the OTP" isSuccess={false} />;
    }
    return <></>;
  }, [notificationState.otpError, notificationState.resendOtp, resendTime]);

  return (
    <FormProvider {...methods}>
      <AuthLayout>
        <Item>
          <Box sx={{ backgroundColor: WsColors.White }} alignItems={'center'}>
            {time != null && time > 0 ? (
              <Grid
                container
                component="form"
                justifyContent="center"
                onSubmit={handleSubmit(onOTPSubmit)}
              >
                <StyledGrid xs={12}>
                  <WisestepLogo />
                </StyledGrid>
                <Grid item textAlign="center" xs={10} py={4} px={2}>
                  <Typography
                    align="center"
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.BLACK}
                  >
                    Please enter the OTP sent to your email
                  </Typography>
                </Grid>
                <StyledGrid item container xs={12} mb={2}>
                  {time != null && (
                    <>
                      OTP Expires in
                      <CountDownTime
                        seconds={time}
                        size={90}
                        strokeBgColor={WsColors.White}
                        strokeColor={WsColors.TheBlues500}
                        strokeWidth={4}
                        onExpiry={() => setTime(null)}
                      />
                    </>
                  )}
                </StyledGrid>
                <StyledGrid item container xs={6} mb={2}>
                  <ControlledTextInput
                    name="otp"
                    type="text"
                    fullWidth
                    id="outlined-basic"
                    placeholder="OTP"
                    variant="outlined"
                  />
                  {showNotification}
                  <WsButton type="submit">Verify</WsButton>
                  {isLoading && <Loader />}
                </StyledGrid>
                <Grid item textAlign="center" xs={12}>
                  {resendTime > 0 ? (
                    <WsTypography>
                      {`Resend OTP in 00:${resendTime < 10 ? `0${resendTime}` : resendTime}`}
                    </WsTypography>
                  ) : (
                    <WsButton type="button" onClick={onResendOTP} variant="text">
                      Resend New OTP
                    </WsButton>
                  )}
                </Grid>
              </Grid>
            ) : (
              <>
                {pageLoaded && (
                  <Grid container justifyContent="center">
                    <Grid item textAlign="center" xs={10} py={4} px={2}>
                      <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK}>
                        Your current OTP has expired !
                      </WsTypography>
                      <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK}>
                        Please request a new OTP by clicking on Resend New OTP button.
                      </WsTypography>
                    </Grid>
                    <WsButton type="button" onClick={onResendOTP} variant="outlined">
                      Resend New OTP
                    </WsButton>
                  </Grid>
                )}
              </>
            )}
            {pageLoaded ? (
              <StyledGrid item container mt={8}>
                <WsTypography
                  display="inline"
                  variant={WsTypoVariants.TITLE}
                  color={WsTypoColors.BLACK}
                >
                  For any help email:-
                </WsTypography>
                <Link sx={{ fontSize: 16 }} href="mailto:support@wisestepmail.com">
                  support@wisestepmail.com
                </Link>
              </StyledGrid>
            ) : (
              <Loader />
            )}
          </Box>
        </Item>
      </AuthLayout>
    </FormProvider>
  );
};
export default OtpPageLayout;
